import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import ServiceIcon from '../ServiceIcon';
import { services } from '../../data/mock';

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-600 text-sm font-medium mb-6">
            Our Solutions
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            AI Systems That{' '}
            <span className="text-emerald-500">Answer, Book & Follow Up</span>
          </h2>
          <p className="text-xl text-gray-600">
            Twelve done-for-you services for Nigerian businesses — from shortlet
            apartments in Lagos to clinics in Abuja and law firms in Port Harcourt.
          </p>
        </div>

        {/* Service Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {services.map((service, index) => {
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(index)}
                className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl font-medium text-sm transition-all duration-300 ${
                  activeService === index
                    ? 'bg-gray-900 text-white shadow-xl scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div style={{ color: activeService === index ? service.color : 'inherit' }}>
                  <ServiceIcon icon={service.icon} className="w-5 h-5" />
                </div>
                <span className="hidden md:inline">
                  {service.title.split(' ').slice(0, 2).join(' ')}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active Service Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-amber-500/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity" />
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={services[activeService].image}
                alt={services[activeService].title}
                className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-xl rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Starting from</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {services[activeService].pricing}
                    </p>
                    {services[activeService].setupFee && (
                      <p className="text-xs text-gray-500 mt-1">
                        {services[activeService].setupFee}
                      </p>
                    )}
                  </div>
                  <Link to={`/services/${services[activeService].id}`}>
                    <Button
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl"
                    >
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
              style={{ backgroundColor: `${services[activeService].color}15` }}
            >
              <div style={{ color: services[activeService].color }}>
                <ServiceIcon icon={services[activeService].icon} />
              </div>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {services[activeService].title}
            </h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {services[activeService].description}
            </p>

            {/* Pain Points */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">Does this sound like you?</h4>
              <ul className="space-y-3">
                {services[activeService].painPoints.map((pain, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                    </span>
                    {pain}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">What you'll get:</h4>
              <ul className="grid sm:grid-cols-2 gap-3">
                {services[activeService].benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-700">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Link to="/contact">
              <Button
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-6 text-lg rounded-xl"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
